// @ts-strict-ignore
import {MPContext} from 'common/mp-context';
import {FeatureGate} from 'common/report/constants';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {generatePermalink} from 'common/report/util/insights-permalink';
import {getAppUrl, getProjectId, getWorkspaceId} from 'common/report/util';
import {getLocation} from 'common/util/url';
import {ActionOption} from 'common/components/filter-bar-table/types';
import {PropertyDefinition} from 'apps/lexicon/src/models/property-definition';
import {isEventDefinition, isPropertyDefinition} from 'apps/lexicon/src/lexicon-app/new-transformations/utils';
import {ActionRule} from 'apps/lexicon/src/lexicon-app/new-transformations/types';
import {GenerateParamsQueryShowClause} from 'common/report/util/types';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';

import {LexiconDefinition, LexiconEntitySpecialCharEscapeMap} from '../types';
import {BulkActions} from './new-transformations/bulk-actions';

export const lexiconUtils = {
  isDefinitionEditable,
  hasHideFeatureEnabled,
};

export function isDefinitionEditable(hasWritePermissions, definition?: LexiconDefinition) {
  // use can_update_basic for custom events and custom properties
  if (isEventDefinition(definition)) {
    return (
      definition?.customEvent?.canUpdateBasic ||
      definition?.canUpdateBasic ||
      isAllowedSuperadminOverride(definition?.customEvent) ||
      isAllowedSuperadminOverride(definition)
    );
  } else if (isPropertyDefinition(definition)) {
    return definition?.canUpdateBasic || isAllowedSuperadminOverride(definition);
  } else if (definition && `can_update_basic` in definition) {
    return definition.can_update_basic;
  }

  return hasWritePermissions;
}

function hasHideFeatureEnabled(context: MPContext) {
  return context.hasFeatureGate(FeatureGate.CanHideEventsAndProperties);
}

export const EVENT_STATUS_FILTER_NAME = `eventStatus`;
export const PROPERTY_STATUS_FILTER_NAME = `propertyStatus`;

export function getSpecialValuesFromUrl(): {
  eventStatusFilterValue: string;
  propertyStatusFilterValue: string;
  mpSource: string;
} {
  const currentUrl = new URL(getLocation().href);

  const eventStatusFilterValue = currentUrl.searchParams.get(EVENT_STATUS_FILTER_NAME);
  const propertyStatusFilterValue = currentUrl.searchParams.get(PROPERTY_STATUS_FILTER_NAME);
  const mpSource = currentUrl.searchParams.get(`mp_source`);

  return {eventStatusFilterValue, propertyStatusFilterValue, mpSource};
}

export function clearValuesFromUrl() {
  const currentUrl = new URL(getLocation().href);
  if (currentUrl.searchParams.has(EVENT_STATUS_FILTER_NAME)) {
    currentUrl.searchParams.delete(EVENT_STATUS_FILTER_NAME);
  }

  if (currentUrl.searchParams.has(`mp_source`)) {
    currentUrl.searchParams.delete(`mp_source`);
  }

  window.history.replaceState(null, null, currentUrl);
}

export function getReportLinkUrl(showClause: GenerateParamsQueryShowClause, reportType = `insights`) {
  return generatePermalink(
    {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      query: {
        dateRange: {
          type: DateRangeType.RelativeAfter,
          window: {
            unit: TimeUnit.Day,
            value: 30,
          },
        },
        showClauses: [showClause],
      },
    },
    false,
    reportType,
  );
}

export function areOnlyNormalPropertiesSelected(selected): boolean {
  return areNormalPropertiesSelected(selected) && !areCustomPropertiesSelected(selected);
}

export function areCustomPropertiesSelected(selected): boolean {
  return selected.some((prop) => !!prop.customPropertyId);
}

export function areNormalPropertiesSelected(selected): boolean {
  return selected.some((prop) => !prop.customPropertyId);
}

export function areOnlyCustomPropertiesSelected(selected): boolean {
  return areCustomPropertiesSelected(selected) && !areNormalPropertiesSelected(selected);
}

export function actionRulesFor(
  action: BulkActions,
  actionRules: Map<BulkActions, ActionRule[]>,
): Partial<ActionOption> {
  const foundRule = actionRules.get(action).find((o) => o.selectorFn());

  // Fail closed for safety
  return foundRule ? foundRule.status : {disabled: true, disabledTooltip: `Unable to do this operation at this time`};
}

export function getSelectedCustomProperties(selected): PropertyDefinition[] {
  return selected.filter((p) => !!p.customPropertyId);
}

export function getTabUrl(tab, key, group = null) {
  if (group) {
    return getAppUrl(`lexicon`) + `/transformations/${tab}/${group}/${encodeURIComponent(key)}`.replace(/\s/g, `-`);
  }
  return getAppUrl(`lexicon`) + `/transformations/${tab}/${encodeURIComponent(key)}`.replace(/\s/g, `-`);
}

/**
 * convert string to all lowercase and replace white space into dash (-)
 */
export function getLowercaseKebabCase(text: string): string {
  return text.toLocaleLowerCase().replace(/\s/g, `-`);
}

// These functions are used to escape/unescape special characters that break React Router/Django for entity names that are used in URLs
export function getEscapedEntityName(entityName?: string): string {
  if (!entityName) {
    return entityName;
  }
  let replacedName = entityName;
  Object.entries(LexiconEntitySpecialCharEscapeMap).forEach(([char, escapeStr]) => {
    replacedName = replacedName.replace(new RegExp(`\\${char}`, `g`), escapeStr);
  });
  return replacedName;
}

export function getUnescapedEntityName(entityName?: string): string {
  if (!entityName) {
    return entityName;
  }
  let replacedName = entityName;
  Object.entries(LexiconEntitySpecialCharEscapeMap).forEach(([char, escapeStr]) => {
    replacedName = replacedName.replace(escapeStr, char);
  });
  return replacedName;
}
